import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { PageHeader } from '@/shared/PageHeader';
import { useAuth } from '@/shared/AuthContext';
import { SelectiveDetailModal } from './SelectiveDetailModal';

interface SelectiveStats {
  total: number;
  byPriority: { priority: string; count: number }[];
  byType: { type: string; count: number }[];
}

export function SelectiveStatsPage(): React.JSX.Element {
  usePageTitle('Statistiche Selettive');
  // Solo l'admin distaccamento puo' aprire il dettaglio di una selettiva
  // (snapshot destinatari + risposte). Per gli altri le righe non sono cliccabili.
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Amministratore', 'SuperAdmin');
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data } = useQuery<SelectiveStats>({
    queryKey: ['selectives.stats'],
    queryFn: async () => (await api.get('/selectives/stats')).data,
  });

  const { data: history } = useQuery({
    queryKey: ['selectives.history'],
    queryFn: async () => (await api.get('/selectives/history')).data,
  });

  const mostFrequent = data?.byType[0]?.type ?? '-';
  const byPriorityMap: Record<string, number> = {};
  (data?.byPriority ?? []).forEach((p) => { byPriorityMap[p.priority] = p.count; });

  return (
    <div className="page-container">
      <PageHeader title={`📈 Statistiche Interventi Selettive ${new Date().getFullYear()}`} />

      <div className="stats-grid stats-cards-fixed">
        <div className="stat-card stat-info">
          <div className="stat-card-title">Totale</div>
          <div className="stat-card-value">{data?.total ?? 0}</div>
        </div>
        <div className="stat-card stat-danger">
          <div className="stat-card-title">Priorita Alta</div>
          <div className="stat-card-value">{byPriorityMap.Alta ?? 0}</div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-card-title">Priorita Media</div>
          <div className="stat-card-value">{byPriorityMap.Media ?? 0}</div>
        </div>
        <div className="stat-card stat-accent">
          <div className="stat-card-title">Tipo piu frequente</div>
          <div className="stat-card-value" style={{ fontSize: 20 }}>{mostFrequent}</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <h3>Storico selettive terminate</h3>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Data</th><th>Tipo</th><th>Priorita</th><th>Zona</th>
                <th>Partecipanti</th><th>Risposte</th><th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(history ?? []).map((h: { id: string; type: string; priority: string; zone?: string; terminatedAt: string; participantCount: number; responseCount: number; terminationReason?: string }) => (
                <tr key={h.id}
                  onClick={isAdmin ? () => setDetailId(h.id) : undefined}
                  style={isAdmin ? { cursor: 'pointer' } : undefined}
                  title={isAdmin ? 'Vedi dettaglio destinatari' : undefined}>
                  <td>{new Date(h.terminatedAt).toLocaleString('it-IT')}</td>
                  <td>{h.type}</td>
                  <td>
                    <span className={`badge badge-${h.priority === 'Alta' ? 'danger' : h.priority === 'Media' ? 'warning' : 'info'}`}>
                      {h.priority}
                    </span>
                  </td>
                  <td>{h.zone ?? '-'}</td>
                  <td>{h.participantCount}</td>
                  <td>{h.responseCount}</td>
                  <td>{h.terminationReason ?? '-'}</td>
                </tr>
              ))}
              {(history ?? []).length === 0 && (
                <tr><td colSpan={7}><div className="empty-state">Nessuna selettiva terminata</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailId && (
        <SelectiveDetailModal selectiveId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}
